import React, { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { ClipboardList, DollarSign, Clock, CheckCircle2, TrendingUp } from "lucide-react";
import { api } from "@/api/client";
import { useManageBusiness } from "@/pages/manage/ManageShell";
import { StatusBadge } from "@/components/StatusTimeline";
import { money } from "@/lib/pricing";
import { getTheme } from "@/lib/theme";

export default function Dashboard() {
  const { businessId } = useManageBusiness();
  const [stats, setStats] = useState(null);
  const [orders, setOrders] = useState([]);
  const [report, setReport] = useState(null);
  const theme = getTheme(businessId);

  useEffect(() => {
    api.getStats(businessId).then(setStats).catch(() => {});
    api.listOrders("all", businessId).then((o) => setOrders(o.slice(0, 6))).catch(() => {});
    api.getSalesReport({ business_id: businessId, days: 7 }).then(setReport).catch(() => {});
  }, [businessId]);

  const cards = [
    { label: "Pending", value: stats?.pending ?? "—", icon: Clock, color: "#8a6100" },
    { label: "Active", value: stats ? (stats.accepted + stats.baking + stats.ready) : "—", icon: ClipboardList, color: "var(--brand)" },
    { label: "Completed", value: stats?.completed ?? "—", icon: CheckCircle2, color: "#12633a" },
    { label: "7-day revenue", value: report ? money(report.total_revenue) : "—", icon: DollarSign, color: "var(--accent)" },
  ];

  return (
    <div>
      <div className="flex items-end justify-between flex-wrap gap-2">
        <div>
          <h1 className="font-display text-3xl" style={{ color: "var(--ink)" }}>Dashboard</h1>
          <p className="text-sm" style={{ color: "var(--muted)" }}>{theme.name}</p>
        </div>
        <Link to="/manage/orders" className="btn-brand px-4 py-2 rounded-full text-sm font-semibold">All orders</Link>
      </div>

      <div className="mt-6 grid gap-4 sm:grid-cols-2 lg:grid-cols-4">
        {cards.map((c) => {
          const Icon = c.icon;
          return (
            <div key={c.label} data-testid={`stat-${c.label.toLowerCase().replace(/\s/g, "-")}`} className="rounded-2xl p-5" style={{ background: "var(--surface)", border: "1px solid var(--border)" }}>
              <div className="flex items-center justify-between">
                <span className="text-sm font-semibold" style={{ color: "var(--muted)" }}>{c.label}</span>
                <Icon size={18} style={{ color: c.color }} />
              </div>
              <div className="font-display text-3xl mt-2" style={{ color: "var(--ink)" }}>{c.value}</div>
            </div>
          );
        })}
      </div>

      {report && (
        <div className="mt-6 rounded-2xl p-5" style={{ background: "var(--surface)", border: "1px solid var(--border)" }}>
          <div className="flex items-center gap-2 mb-4">
            <TrendingUp size={18} style={{ color: "var(--brand)" }} />
            <h2 className="font-display text-xl" style={{ color: "var(--ink)" }}>This week</h2>
          </div>
          <div className="flex items-end gap-2 h-32">
            {report.daily.map((d) => {
              const max = Math.max(...report.daily.map((x) => x.revenue), 1);
              const h = (d.revenue / max) * 100;
              return (
                <div key={d.date} className="flex-1 flex flex-col items-center gap-1.5">
                  <div className="w-full rounded-t-md transition-all" style={{ height: `${Math.max(h, 4)}%`, background: "var(--brand)", opacity: d.revenue ? 1 : 0.25 }} title={money(d.revenue)} />
                  <span className="text-[10px]" style={{ color: "var(--muted)" }}>{d.label}</span>
                </div>
              );
            })}
          </div>
        </div>
      )}

      <div className="mt-6 rounded-2xl p-5" style={{ background: "var(--surface)", border: "1px solid var(--border)" }}>
        <h2 className="font-display text-xl mb-4" style={{ color: "var(--ink)" }}>Recent orders</h2>
        {orders.length === 0 ? (
          <p className="text-sm" style={{ color: "var(--muted)" }}>No orders yet.</p>
        ) : (
          <div className="space-y-2">
            {orders.map((o) => (
              <Link key={o.id} to="/manage/orders" className="flex items-center justify-between py-2.5 px-3 rounded-xl hover:bg-[var(--surface-2)] transition">
                <div>
                  <span className="font-semibold" style={{ color: "var(--ink)" }}>{o.order_number}</span>
                  <span className="text-xs ml-2" style={{ color: "var(--muted)" }}>{o.customer_name}</span>
                </div>
                <div className="flex items-center gap-3">
                  <span className="font-semibold" style={{ color: "var(--ink)" }}>{money(o.total)}</span>
                  <StatusBadge status={o.status} />
                </div>
              </Link>
            ))}
          </div>
        )}
      </div>
    </div>
  );
}
